import {defineStore} from "pinia";
import {reactive, ref, computed, getCurrentInstance, onUnmounted, ComputedRef, shallowReactive, shallowRef, Ref, type InjectionKey, inject, provide} from "vue";
import {env} from "@fd/Environment.ts";
import {
	MenuGroup,
	normalizeTopMenu,
	type SideMenu,
	TopMenu,
	SideMenuChanges,
	MenuChange,
	findGroupPosition,
	findItemPosition
} from "@t/ui/Menu.ts";
import type {History, HistoryObject} from "@t/meta/History.ts";
import type {Stacker, StackPage} from "@t/meta/Stacker.ts";
import {setStackerIndex} from "@/utils/Stacker.ts";
import {useRoute, type LocationQueryRaw} from "vue-router";
import {EventBus} from "@fd/Types.ts";
import {
	addContextParams,
	AppContextMap,
	mergeMaps,
	sameContext
} from "@t/fd/AppContext.ts";
import {OfferConcept} from "@t/offer/Offer.ts";
import {BusCaseType} from "@t/buscase/BusCase.ts";
import {lazyLoad} from "@/store/Store.ts";
import {getHash, goParams, parseLocalUrl, removeParams, routeActiveSection, router, toSubContextPath} from "@/router/Router.ts";
import {escapeHtml} from "@t/util/SafeHtml.ts";
import {ActionPanelInstance} from "@t/ui/ActionPanel.ts";
import {SharedContextDirect} from "@/chat/ChatClient.ts";
import {RowObjectRef, RowObjectType} from "@t/meta/SourceType.ts";
import {ListItemWitIcon, MenuBuilder, MenuModFunc, PreloadedMenuItems} from "@/store/MenuBuilder.ts";
import {CREATE, EDIT, REPLACE_INIT_WITH_EDIT} from "@/pages/row-object/RowObjectEvents.ts";

export interface OptionProvider {
	/** Return value of the option or undefined if this provider doesn't handle the option */
	get: (name:string) => boolean|undefined;
	set: (name:string, value: boolean) => void;
}


function emptySideMenu():SideMenu {
	return { groups: [] };
}

/** Structure to register menu contributor using defineMenu() */
interface SideMenuContributor {
	/** Menu building function */
	apply: MenuModFunc,
	options?: OptionProvider,

	/**
	 * Optional local app context
	 * Note: this context is overwritten with primary context on conflicting values. Local context can just append values.
	 **/
	ctx?: ComputedRef<AppContextMap>
}


let sideMenuDebugCount = 0;
const SIDE_MENU_DEBUG_LIMIT = 20;

function debugSideMenu(message: string): boolean {
	if (sideMenuDebugCount < SIDE_MENU_DEBUG_LIMIT) {
		console.log(message);
		sideMenuDebugCount++;
		return true;	// continue
	}
	return false;
}

/** This serves to pick the top-most component which panages stacker on edit, init, replace */
const stackerManipulatorKey = Symbol() as InjectionKey<boolean>;

export const useNavigationStore = defineStore('navigation', () => {
	const route = useRoute();

	const activeSection = computed<string>(() => {
		return routeActiveSection(route);
	});

	// Menu collections
	const { data: offerConcepts} = lazyLoad<OfferConcept[]>(async () => {
		let ep = "api/offers/concepts";
		const resp = await env.net.fetch(ep);
		if (resp.ok) return await resp.json() as OfferConcept[];
		throw new Error(JSON.stringify(resp));
	}, []);

	const { data: busCaseTypes } = lazyLoad<BusCaseType[]>(async () => {
		let ep = "api/buscases/types";
		const resp = await env.net.fetch(ep);
		if (resp.ok) return await resp.json() as BusCaseType[];
		throw new Error(JSON.stringify(resp));
	}, []);

	const { data: problemTypes } = lazyLoad<ListItemWitIcon[]>(async () => {
		let ep = "api/problems/types";
		const resp = await env.net.fetch(ep);
		if (resp.ok) return await resp.json() as ListItemWitIcon[];
		throw new Error(JSON.stringify(resp));
	}, []);


	// TOP NAVIGATION
	const topMenu = ref<TopMenu>({
		sections: []
	});

	const chatContext = ref<SharedContextDirect|null>(null);

	function setTopMenu(data: TopMenu):void {
		data = normalizeTopMenu(data);
		topMenu.value.sections.length = 0;
		for (let sec of data.sections) topMenu.value.sections.push(sec);
	}

	async function loadTopMenu(): Promise<any> {
		let endpoint = "api/navigation/top";
		return env.net.fetch(endpoint)
			.then((resp) => {
				if (resp.ok) {
					resp.json().then(setTopMenu);
				}
			});
	}

	async function setOrLoadTopMenu(data?:TopMenu): Promise<any> {
		if (data) {
			setTopMenu(data);
			return Promise.resolve(data);
		}
		return loadTopMenu();
	}

	// SIDEBAR MENU
	const menuDefinitions = shallowReactive(new Map<Symbol, SideMenuContributor>());

	const sideMenuChanges = ref<SideMenuChanges|null>(null);	// side menu modifications from the server

	/// Composite app context merged from menu contributors and primary context
	const pageAppContext = computed<AppContextMap>(() => {
		const ctx: AppContextMap = {};
		for (let def of menuDefinitions.values()) {
			if (def.ctx?.value != null) mergeMaps(ctx, def.ctx.value);
		}
		if (primaryContext.value?.value != null) {
			mergeMaps(ctx, primaryContext.value.value);
		}
		return ctx;
	});

	const sideMenu = computed<SideMenu>(() => {
		let menu:SideMenu = { groups: [] };
		//if (!debugSideMenu("------ computing side menu, contributors = "+menuDefinitions.size)) return menu;
		const preloads:PreloadedMenuItems = {
			offerConcepts,
			busCaseTypes,
			problemTypes
		};
		let mb = new MenuBuilder(env, preloads, menu, primaryRowObject.value?.value ?? { type: 0, id: 0}, pageAppContext.value);
		//let counter = 0;
		for (let def of menuDefinitions.values()) {
			def.apply(mb);
			//counter++;
		}
		//console.log("...rebuilt side menu using " + counter + " definitions. pages: " + JSON.stringify(mb.getPageNames()));
		detectPageNamesChange(mb.getPageNames(), pageAppContext.value);
		if (sideMenuChanges.value && sideMenuChanges.value.changes.length > 0) applyMenuChanges(menu, sideMenuChanges.value);
		return menu;
	});

	let lastPageNames = new Set<string>();
	let lastAppContext:AppContextMap = {};
	/** This function invokes sideMenuChanges load when pageName set changes from last time */
	function detectPageNamesChange(names:Set<string>, ctx:AppContextMap) {
		if (setsEqual(lastPageNames, names) && sameContext(lastAppContext, ctx)) return;
		lastPageNames = new Set(names);
		lastAppContext = ctx == null ? {} : { ...ctx };
		loadSideMenuChanges(lastPageNames, lastAppContext);
	}

	function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
		if (a.size !== b.size) return false;
		for (const item of a) {
			if (!b.has(item)) return false;
		}
		return true;
	}

	const EXTENSION_MENU_GROUP:MenuGroup = { name:"menu_ext", open:"open", title: env.msg('menu.plugin'), items:[] };


	const showHistory = ref<boolean>(true);
	const historyLimit = ref<number>(15);
	const historyItems:HistoryObject[] = reactive<HistoryObject[]>([]);
	const actionPanels:ActionPanelInstance<any>[] = reactive<ActionPanelInstance<any>[]>([]);

	let sideMenuChangesTimer = 0;
	function loadSideMenuChanges(pageNames: Set<string>, ctx:AppContextMap) {
		if (pageNames.size === 0) {
			// Empty page name set (no need to call the server)
			sideMenuChanges.value = null;
			return;
		}
		if (sideMenuChangesTimer !== 0) clearTimeout(sideMenuChangesTimer);
		sideMenuChangesTimer = setTimeout(async () => {
			sideMenuChangesTimer = 0;
			const page: string = [...pageNames].join(",");
			let endpoint = env.net.urlParam("api/navigation/menu", "page", page);
			// Include action panels in the response
			endpoint = env.net.urlParam(endpoint, "panels", "true");
			if (ctx) endpoint = addContextParams(endpoint, ctx);

			const nr = await env.net.fetch(endpoint);
			if (nr.ok) {
				if (nr.status === 200) {
					// There is a new version of side menu
					const chs:SideMenuChanges = await nr.json() as SideMenuChanges;
					if (Array.isArray(chs.panels)) {
						setActionPanels(chs.panels);
						delete chs.panels;
					}
					sideMenuChanges.value = chs;
				}
			}
		}, 500);
	}

	async function loadActionPanels():Promise<any> {
		let endpoint = "api/actionpanel";
		const resp = await env.net.fetch(endpoint);
		if (resp.ok) {
			setActionPanels(await resp.json() as ActionPanelInstance<any>[]);
		}
	}

	function applyMenuChanges(m:SideMenu, ch:SideMenuChanges):void {
		for (let change of ch.changes) {
			applyMenuChange(m, change);
		}
	}

	function targetMenuGroup(m:SideMenu, name:string):MenuGroup {
		let idx = findGroupPosition(m, name);
		if (idx < 0) idx = findGroupPosition(m, EXTENSION_MENU_GROUP.name);
		if (idx < 0) {
			m.groups.push({ ...EXTENSION_MENU_GROUP, items: []});
			idx = m.groups.length - 1;
		}
		return m.groups[idx];
	}

	function applyMenuChange(m:SideMenu, ch:MenuChange):void {
		switch (ch.op) {
			case "append":
				if (ch.item) {
					// ref is group name
					const mg:MenuGroup = targetMenuGroup(m, ch.ref);
					mg.items.push(ch.item);
				} else if (ch.group) {
					m.groups.push(ch.group);
				}
				break;
			case "prepend":
				if (ch.item) {
					// ref is group name
					const mg:MenuGroup = targetMenuGroup(m, ch.ref);
					mg.items.splice(0, 0, ch.item);
				} else if (ch.group) {
					m.groups.splice(0, 0, ch.group);
				}
				break;
			case "insert_before":
				if (ch.item) {
					const pos = findItemPosition(m, ch.ref);
					if (pos) {
						pos.list.splice(pos.index, 0, ch.item);
					} else {
						targetMenuGroup(m, EXTENSION_MENU_GROUP.name).items.push(ch.item);
					}
				} else if (ch.group) {
					let idx = findGroupPosition(m, ch.ref);
					m.groups.splice(Math.max(0, idx), 0, ch.group);
				}
				break;
			case "insert_after":
				if (ch.item) {
					const pos = findItemPosition(m, ch.ref);
					if (pos) {
						pos.list.splice(pos.index+1, 0, ch.item);
					} else {
						targetMenuGroup(m, EXTENSION_MENU_GROUP.name).items.push(ch.item);
					}
				} else if (ch.group) {
					let idx = findGroupPosition(m, ch.ref);
					if (idx >= 0) m.groups.splice(idx+1, 0, ch.group);
					else m.groups.push(ch.group);
				}
				break;
			case "remove":
				if (ch.remove === "item") {
					const pos = findItemPosition(m, ch.ref);
					if (pos) pos.list.splice(pos.index, 1);
				} else if (ch.remove === "group") {
					let idx = findGroupPosition(m, ch.ref);
					if (idx >= 0) m.groups.splice(idx, 1);
				}
				break;
		}
	}

	/**
	 * Sidebar can contain menu switches (options)
	 * Values of these options are accessed through these get/setOptions functions
	 */
	function getOption(name:string):boolean|undefined {
		for (let def of menuDefinitions.values()) {
			if (def.options) {
				const value = def.options.get(name);
				if (typeof value === 'boolean') return value;
			}
		}
		return undefined;	// option not found
	}

	function setOption(name:string, value: boolean):void {
		for (let def of menuDefinitions.values()) {
			if (def.options) {
				def.options.set(name, value);
			}
		}
	}

	function setHistoryItems(items:HistoryObject[]):void {
		historyItems.length = 0;
		historyItems.push(...items);
	}

	function setActionPanels(panels:ActionPanelInstance<any>[]):void {
		actionPanels.length = 0;
		actionPanels.push(...panels);
	}

	/**
	 * Execute backend action for given panel
	 * @param panelId ID of the panel
	 * @param action name of the action
	 * @param params optional request parameters
	 * @param data optional payload (POST will be executed with this data)
	 */
	async function panelAction(panelId: string, action: string, params:Record<string, number|string|boolean> = {}, data: Record<string, any> = null): Promise<boolean> {
		let ep = "api/actionpanel/"+panelId;
		ep = env.net.urlParam(ep, "action", action);
		for (let k of Object.keys(params)) {
			ep = env.net.urlParam(ep, k, params[k]);
		}
		const resp = await env.net.fetch(ep, data == null ? "GET" : "POST", data);
		if (resp.ok) {
			const panels = await resp.json() as ActionPanelInstance<any>[];
			setActionPanels(panels);
			return true;
		}
		return false;
	}

	async function savePanelComment(panelId:string, comment:string):Promise<boolean> {
		return await panelAction(panelId, "comment", {}, { comment: comment });
	}

	const hasHistory = computed<boolean>(() => historyItems.length > 0);

	function reloadHistory() {
		env.net.fetch("api/history?limit="+historyLimit.value)
			.then((response) =>  response.json())
			.then((hist:History) => {
				setHistoryItems(hist.items);
			});
	}

	const displaySrcType = ref<number>(0);
	const displaySrcSubType = ref<number>(0);
	const displaySrcId = ref<number|string>(0);

	/** Insert a new item into history and reload current history list */
	function display(srcType:number, srcId:number|string, srcSubType?:number):void {
		let ep = "api/history/display?limit="+historyLimit.value;
		ep = env.net.urlParam(ep, "type", srcType);
		ep = env.net.urlParam(ep, "id", srcId);
		if (srcSubType) ep = env.net.urlParam(ep, "subType", srcSubType);
		displaySrcType.value = srcType;
		displaySrcId.value = srcId;
		displaySrcSubType.value = srcSubType ?? 0;
		env.net.fetch(ep)
			.then((response) =>  response.json())
			.then((hist:History) => {
				setHistoryItems(hist.items);
			});
	}

	/** Clear information about displayed object */
	function undisplay():void {
		displaySrcType.value = 0;
		displaySrcId.value = 0;
		displaySrcSubType.value = 0;
	}

	function isOnDisplay(srcType:number, srcId:number|string, srcSubType?:number):boolean {
		if (displaySrcType.value !== srcType) return false;
		if (srcSubType && displaySrcSubType.value !== srcSubType) return false;
		return displaySrcId.value == srcId;		// this returns true if one is string and the other number ("3" == 3)
	}

	function dirtyHistory():void {
		setTimeout(reloadHistory, 1000);
	}


	// STACKER
	const stacker:StackPage[] = reactive<StackPage[]>([]);
	const stackerLoaded = ref(false);

	function loadStacker(page?:StackPage, pop = -1, at:number = -1) {
		let endpoint = "api/stacker";
		if (pop > 0) endpoint = env.net.urlParam(endpoint, "pop", pop);
		else if (at >= 0) endpoint = env.net.urlParam(endpoint, "at", at);
		const connect = page ? env.net.fetch(endpoint, "POST", page) : env.net.fetch(endpoint);
		connect
			.then((response) =>  response.json())
			.then((st:Stacker) => {
				initStacker(st);
			});
	}
	
	function initStacker(st: Stacker): void {
		//console.dir(st);
		stacker.length = 0;
		let index = -1;
		for (let pg of st.pages) {
			stacker.push(pg);
			index++;
		}
		if (index >= 0) setStackerIndex(route, index);
		stackerLoaded.value = true;
		setPageTitle();
	}
		

	function setPageTitle() {
		if (stacker.length > 0) {
			const name = stacker[stacker.length - 1].title;
			const template = Server.title;
			const title = template.replace("{title}", escapeHtml(name));
			const titleElement = document.querySelector("html >  head > title") as HTMLTitleElement;
			if (titleElement) {
				titleElement.innerText = title;
			}
		}
	}

	function removeFromStacker(page: StackPage) :Promise<boolean> {
		//console.log("removing:"+JSON.stringify(page));
		return env.net.fetch("api/stacker", "DELETE", page)
			.then((response) =>  response.json())
			.then((st:Stacker) => {
				initStacker(st);
				return true;
			})
			.catch(()=>{
				return false;
			});
	}
	
	function pagePath(sp:StackPage, idx: number) :string {
		if (sp.path) {
			let path = sp.path;
			if (Server.context && Server.context !== "/") path = Server.context + path;
			if (sp.attributes) {
				for (let key of Object.keys(sp.attributes)) {
					path = env.net.urlParam(path, key, sp.attributes[key]);
				}
			}
			path = env.net.urlParam(path, "_sidx_", idx);
			return path;
		}
		return "#";
	}

	function previousPagePathFromStacker() :string {
		//console.log("removing:"+JSON.stringify(page));
		let index = stacker.length > 2 ? stacker.length-2 : 0;
		return pagePath(stacker[index], index);
	}

	/**
	 * Construct StackPage from current route
	 */
	function currentStackPage(name:string):StackPage {
		const sp:StackPage = { title: name };
		if (route.path) {
			sp.path = route.path;
			if (Server.context && Server.context !== "/") {
				if (sp.path.startsWith(Server.context)) sp.path = sp.path.substring(Server.context.length);
			}
		}
		if (route.hash) sp.anchor = route.hash;
		const at = {};
		let hasAttrs = false;
		for (let k of Object.keys(route.query)) {
			if (k === "_sidx_") continue;		// this is special param for managing stacker (ignored here)
			at[k] = route.query[k];
			hasAttrs = true;
		}
		if (hasAttrs) sp.attributes = at;
		return sp;
	}

	/**
	 * Function similar to currentStackPage but this doesn't you information about
	 * current route from Vue Router but the info is passed to the function
	 */
	function prepareStackPage(name:string, path:string, params?:LocationQueryRaw):StackPage {
		const hash = getHash(path);
		const sp:StackPage = { title: name, path: toSubContextPath(removeParams(path)) };
		if (hash) sp.anchor = hash;
		const at = {};
		let hasAttrs = false;
		if (params) {
			for (let k of Object.keys(params)) {
				if (k === "_sidx_") continue;		// this is special param for managing stacker (ignored here)
				at[k] = params[k];
				hasAttrs = true;
			}
		}
		if (hasAttrs) sp.attributes = at;
		return sp;
	}

	function forward(name:string):void {
		const idx:number = stackerIndexFromRoute();
		//console.log("Forward idx=" + idx);
		loadStacker(currentStackPage(name), -1, idx);
	}

	function replace(name:string):void {
		const idx = stackerIndex();
		const page = currentStackPage(name);
		if (idx >= 0) loadStacker(page, -1, idx);
		else loadStacker(page, 1);
	}

	function back():void {
		//console.log("calling router.back() ========");
		router.back();
	}

	/** Return current stacker index from loaded stack, or -1 if no path was loaded yet */
	function stackerIndexFromPath():number {
		if (stackerLoaded.value && stacker.length > 0) {
			return stacker.length - 1;
		}
		return -1;
	}

	/** Return current stacker index from Vue Router route (query param), or -1 if no _sidx_ param is there */
	function stackerIndexFromRoute():number {
		//console.log("stackerIndexFromRoute(): " + route.fullPath+", _sidx_="+route.query._sidx_);
		return parseIndex(route.query._sidx_, -1);
	}

	/** Return current stacker index from window.location.href, or -1 if no _sidx_ param is there */
	function stackerIndexFromHref():number {
		const parsed = parseLocalUrl(window.location.href);
		return parseIndex(parsed.query._sidx_, -1);
	}

	/**
	 * Return index of current page in stacker.
	 * The index can be determined
	 * a) from length of loaded stack
	 * b) from _sidx_ param
	 */
	function stackerIndex():number {
		if (stackerLoaded.value && stacker.length > 0) {
			return stacker.length - 1;
		}
		return parseIndex(route.query._sidx_, -1);
	}

	function parseIndex(idx:string|string[]|null|undefined, defalt: number):number {
		if (idx == null || idx.length === 0) return defalt;
		if (Array.isArray(idx)) idx = idx[0];
		return parseInt(idx);
	}

	function replaceWith(path:string, params?:LocationQueryRaw, name?:string):void {
		let currentIndex = stackerIndex();
		if (!params) params = {};
		if (currentIndex >= 0) params["_sidx_"] = currentIndex;
		if (goParams(path, params, true) && name) {
			// We're staying on this page... so we have to update stacker
			const page = prepareStackPage(name, path, params);
			if (currentIndex >= 0) loadStacker(page, -1, currentIndex);
			else loadStacker(page, 1);
		}
	}

	function stay():void {
		if (stackerLoaded.value) return;
		loadStacker();
	}

	const primaryContext = shallowRef<ComputedRef<AppContextMap> | null>(null);
	const primaryRowObject = shallowRef<ComputedRef<RowObjectRef> | null>(null);
	const pageName = ref<string | null>(null);

	function definePage(page:string, oref:ComputedRef<RowObjectRef>, ctx:ComputedRef<AppContextMap>|null = null):void {
		pageName.value = page;
		primaryContext.value = ctx;
		primaryRowObject.value = oref;
		onUnmounted(() => {
			if (pageName.value === page) {
				pageName.value = null;
				primaryContext.value = null;
				primaryRowObject.value = null;
			}
		});
	}

	function defineMenu(fn:MenuModFunc, options?:OptionProvider, localCtx:ComputedRef<AppContextMap> = null):void {
		if (!getCurrentInstance()) {
			console.warn('defineMenu() must be called inside setup()');
			return;
		}
		const defId = Symbol('menu-def');
		let registration:SideMenuContributor = { apply: fn };
		if (options) registration.options = options;
		if (localCtx != null) registration.ctx = localCtx;
		menuDefinitions.set(defId, registration);
		onUnmounted(() => {
			menuDefinitions.delete(defId);
		});
	}

	/** Update stacker paths by automatically listening for dialog events */
	function listenForStackerUpdates(bus:EventBus, listenerOref:Ref<RowObjectType>,
									 createName:(() => string)|null,
									 editPath: string,
									 editName:(() => string)|null,
									 editId:(() => string|number)|null) {
		const isTopMost = inject(stackerManipulatorKey, true);

		//console.log(editPath+" stacker topmost: " + isTopMost);
		if (isTopMost) {
			provide(stackerManipulatorKey, false);	// prevent child components to touch stacker

			// Only the top-most component gets a chance to update stacker
			function doCreate(oref:RowObjectType) {
				//console.log(editPath+" doCreate called:", oref);
				if (oref.type === listenerOref.value.type && createName != null) {
					forward(createName());
				}
			}

			function doEdit(oref:RowObjectRef) {
				//console.log(editPath+" doEdit called:", oref);
				if (oref.type === listenerOref.value.type && editName != null) {
					forward(editName());
				}
			}

			function doReplace(oref:RowObjectRef) {
				//console.log(editPath+" doReplace called:", oref);
				if (editPath != null && editName != null && editId != null) {
					let id = editId();
					if (oref.type === listenerOref.value.type && oref.id === id) replaceWith(editPath, { id: id }, editName());
				}
			}

			if (createName != null) bus.on(CREATE, doCreate);
			if (editName != null) bus.on(EDIT, doEdit);
			if (editPath != null && editName != null && editId != null) bus.on(REPLACE_INIT_WITH_EDIT, doReplace);
		}
	}

	return {
		topMenu, loadTopMenu, setTopMenu, setOrLoadTopMenu,
		sideMenu, getOption, setOption,

		showHistory, historyItems, reloadHistory, dirtyHistory, setHistoryItems, display, undisplay,
		displaySrcType, displaySrcId, displaySrcSubType, isOnDisplay, historyLimit,
		stacker, stackerLoaded, loadStacker, forward, replace, replaceWith, stay, stackerIndex,

		defineMenu, definePage, pageAppContext, listenForStackerUpdates,

		setActionPanels, actionPanels, panelAction, savePanelComment, loadActionPanels,

		activeSection, hasHistory,

		offerConcepts, busCaseTypes, problemTypes,

		/** Use to provide a context to the chat client window */
		chatContext,

		back,
		removeFromStacker,
		previousPagePathFromStacker
	};
});